import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, LoginCredentials, AuthResponse, UserRole } from '../types';
import { authService } from '../services/authService';

interface AuthContextData {
  user: User | null;
  loading: boolean;
  signIn: (credentials: LoginCredentials, userType?: UserRole) => Promise<void>;
  completeAuth: (response: AuthResponse) => void;
  signOut: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isAssociate: boolean;
  isDependent: boolean;
  userRole: UserRole | null;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStoredData = () => {
      const storedUser = authService.getStoredUser();
      const storedToken = authService.getStoredToken();

      if (storedUser && storedToken) {
        setUser(storedUser);
      }

      setLoading(false);
    };

    loadStoredData();
  }, []);

  const signIn = async (credentials: LoginCredentials, userType?: UserRole) => {
    // Propaga o erro original (com `error.response`) para quem chamou poder
    // inspecionar status/código (ex.: 403 "senha_nao_definida" do dependente).
    const response: AuthResponse = await authService.login(credentials, userType);
    authService.saveAuth(response);
    setUser(response.user);
  };

  // Usado quando o token já foi obtido fora do fluxo normal de login
  // (ex.: dependente definindo a senha no primeiro acesso).
  const completeAuth = (response: AuthResponse) => {
    authService.saveAuth(response);
    setUser(response.user);
  };

  const signOut = () => {
    authService.logout();
    setUser(null);
  };

  const userRole: UserRole | null = user?.user_type || null;
  const isAdmin = user?.user_type === 'admin' || user?.is_master === true;
  const isAssociate = user?.user_type === 'associate';
  const isDependent = user?.user_type === 'dependent';

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signIn,
        completeAuth,
        signOut,
        isAuthenticated: !!user,
        isAdmin,
        isAssociate,
        isDependent,
        userRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
